/**
 * useMultiFileUpload — stage and upload many files in parallel.
 *
 * Given an injected uploader (see `UploadFileFn`), this hook:
 *   - caps the batch size (defaults to MAX_UPLOADS)
 *   - drops files above the configured size
 *   - drops files whose MIME type isn't in `acceptMimePrefixes`
 *   - runs uploads concurrently with a bounded pool
 *   - exposes per-file status so the caller can render progress UI
 *
 * The hook is intentionally framework-free: callers supply the actual
 * `ingest` call that talks to their backend.
 */
import { useCallback, useRef, useState } from "react";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Upper bound on how many files a single drop can contain. */
export const MAX_UPLOADS = 20;
/** Upper bound on a single file's size. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB
/** Parallelism cap for the uploader pool. */
const CONCURRENCY = 4;
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useMultiFileUpload(options) {
    const { uploadFile, acceptMimePrefixes = ["image/"], onFileComplete, onBatchComplete } = options;
    const [uploads, setUploads] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    // Ref mirror — we need synchronous mutation inside the worker loop while
    // still driving React re-renders.
    const uploadsRef = useRef([]);
    const commit = useCallback((next) => {
        uploadsRef.current = next;
        setUploads(next);
    }, []);
    const updateOne = useCallback((id, patch) => {
        const next = uploadsRef.current.map((u) => (u.id === id ? { ...u, ...patch } : u));
        uploadsRef.current = next;
        setUploads(next);
    }, []);
    const uploadFiles = useCallback(async (files, context) => {
        const incoming = Array.from(files);
        const accepted = [];
        for (const file of incoming) {
            if (accepted.length >= MAX_UPLOADS)
                break;
            const typeOk = acceptMimePrefixes.some((p) => file.type.startsWith(p));
            if (typeOk === false)
                continue;
            if (file.size > MAX_FILE_BYTES)
                continue;
            accepted.push({
                id: `upl_${Date.now()}_${accepted.length}_${Math.random().toString(36).slice(2, 8)}`,
                file,
                status: "pending",
            });
        }
        if (accepted.length === 0)
            return;
        commit(accepted);
        setIsUploading(true);
        // Bounded-concurrency worker pool — simple queue index, each worker
        // pulls the next pending upload until the queue is drained.
        let cursor = 0;
        let succeeded = 0;
        let failed = 0;
        const worker = async () => {
            while (cursor < accepted.length) {
                const i = cursor++;
                const item = accepted[i];
                if (!item)
                    continue;
                updateOne(item.id, { status: "uploading" });
                try {
                    await uploadFile({
                        file: item.file,
                        ...(context?.collectionId !== undefined ? { collectionId: context.collectionId } : {}),
                    });
                    updateOne(item.id, { status: "success" });
                    succeeded += 1;
                    onFileComplete?.(item.file);
                }
                catch (e) {
                    const message = e instanceof Error ? e.message : "Upload failed";
                    updateOne(item.id, { status: "error", error: message });
                    failed += 1;
                }
            }
        };
        const poolSize = Math.min(CONCURRENCY, accepted.length);
        await Promise.all(Array.from({ length: poolSize }, () => worker()));
        setIsUploading(false);
        onBatchComplete?.({ total: accepted.length, succeeded, failed });
    }, [uploadFile, acceptMimePrefixes, onFileComplete, onBatchComplete, commit, updateOne]);
    const clear = useCallback(() => commit([]), [commit]);
    return { uploads, isUploading, uploadFiles, clear };
}
